// apps/web/src/features/crm/leads/activity/components/LeadCancelledTasksSubsection.tsx
//
// Collapsed cancelled-tasks subsection inside the tasks SectionCard (#2317 D-3a).
// Editor-only (the parent passes `enabled = canEditCard ∧ tasks.manage`); it
// stays dormant — no query, no render — until enabled, and renders nothing
// when there are no cancelled tasks so a clean lead shows no empty control
// (BR-6). Collapsed by default; expanding lists each cancelled MANUAL task
// with a REOPEN control that restores it to the open list.
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CancelledLeadTask } from '@magimp/shared';
import { useCancelledLeadTasks, useReopenLeadTask } from '@magimp/shared-hooks';
import { Button, Icon } from '../../../../../ui/index.js';
import { formatDateTime } from '../../../../../i18n/index.js';
import { useToastStore } from '../../../../../stores/toast.js';
import { errorMessage } from '../../../../../api/errors.js';

export interface LeadCancelledTasksSubsectionProps {
  leadId: string;
  enabled: boolean;
}

export function LeadCancelledTasksSubsection({
  leadId,
  enabled,
}: LeadCancelledTasksSubsectionProps) {
  const { t } = useTranslation(['leads']);
  const [open, setOpen] = useState(false);
  const q = useCancelledLeadTasks(leadId, { enabled });

  const items = q.data?.items ?? [];
  if (!enabled || !q.isSuccess || items.length === 0) return null;

  return (
    <div data-testid="lead-cancelled-tasks" style={{ marginTop: 12 }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid="lead-cancelled-tasks-toggle"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          width: '100%',
          background: 'transparent',
          border: 0,
          padding: '6px 0',
          cursor: 'pointer',
          font: 'inherit',
          color: 'var(--fg-3)',
          fontSize: 'var(--fz-12)',
        }}
      >
        <Icon name={open ? 'chevron-down' : 'chevron-right'} size={14} />
        <span>{t('leads:tasks.cancelled.title')}</span>
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{items.length}</span>
      </button>
      {open && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
          {items.map((task) => (
            <CancelledTaskRow key={task.id} task={task} leadId={leadId} />
          ))}
        </div>
      )}
    </div>
  );
}

function CancelledTaskRow({ task, leadId }: { task: CancelledLeadTask; leadId: string }) {
  const { t } = useTranslation(['leads']);
  const show = useToastStore((s) => s.show);
  const reopen = useReopenLeadTask(task.id, leadId);

  const onReopen = () => {
    reopen.mutate(undefined, {
      onError: (err) => show({ tone: 'error', text: errorMessage(err) }),
    });
  };

  return (
    <div
      data-testid="lead-cancelled-task-row"
      data-task-id={task.id}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: 8,
        borderRadius: 'var(--r-2)',
        border: '1px solid var(--border-2)',
        background: 'var(--bg-2)',
      }}
    >
      <span
        style={{
          flex: 1,
          minWidth: 0,
          fontSize: 12,
          color: 'var(--fg-3)',
          textDecoration: 'line-through',
          textDecorationColor: 'var(--fg-4)',
          wordBreak: 'break-word',
        }}
      >
        {task.title}
      </span>
      <time dateTime={task.cancelledAt} style={{ fontSize: 11, color: 'var(--fg-4)' }}>
        {formatDateTime(task.cancelledAt)}
      </time>
      <Button
        size="sm"
        kind="ghost"
        icon="rotate-ccw"
        onClick={onReopen}
        disabled={reopen.isPending}
        data-testid="lead-task-reopen"
      >
        {t('leads:tasks.actions.reopen')}
      </Button>
    </div>
  );
}
